import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  Box,
} from "@material-ui/core";
import { toast } from "react-toastify";

const TIPOS = [
  { v: "cpf", t: "CPF", ex: "000.000.000-00" },
  { v: "cnpj", t: "CNPJ", ex: "00.000.000/0000-00" },
  { v: "telefone", t: "Telefone", ex: "(11) 99999-9999" },
  { v: "email", t: "E-mail", ex: "voce@email.com" },
  { v: "aleatoria", t: "Chave Aleatória", ex: "123e4567-e12b-12d1-a456-426655440000" },
];

// Confere o formato antes de salvar — chave errada é dinheiro no lugar errado.
const conferirChave = (tipo, valor) => {
  const v = String(valor || "").trim();
  const d = v.replace(/[^\d]/g, "");
  if (!v) return "Informe a chave Pix.";
  if (tipo === "cpf" && d.length !== 11) return "CPF precisa ter 11 dígitos.";
  if (tipo === "cnpj" && d.length !== 14) return "CNPJ precisa ter 14 dígitos.";
  if (tipo === "telefone" && (d.length < 10 || d.length > 13))
    return "Telefone precisa ter DDD + número.";
  if (tipo === "email" && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v))
    return "E-mail inválido.";
  return "";
};

const FlowBuilderPixModal = ({ open, onSave, onUpdate, data, close }) => {
  const [activeModal, setActiveModal] = useState(false);
  const [tipoChave, setTipoChave] = useState("telefone");
  const [chave, setChave] = useState("");
  const [nomeRecebedor, setNomeRecebedor] = useState("");
  const [valor, setValor] = useState("");
  const [formato, setFormato] = useState("cartao");
  const [bodyText, setBodyText] = useState("");
  const [comoFunciona, setComoFunciona] = useState(false);

  const tipoAtual = TIPOS.find((t) => t.v === tipoChave) || TIPOS[2];

  useEffect(() => {
    if (open === "edit") {
      const d = data?.data || data || {};
      setTipoChave(d.tipoChave || "telefone");
      setChave(d.chave || "");
      setNomeRecebedor(d.nomeRecebedor || "");
      setValor(d.valor || "");
      setFormato(d.formato || "cartao");
      setBodyText(d.bodyText || "");
      setComoFunciona(false);
      setActiveModal(true);
    } else if (open === "create") {
      setTipoChave("telefone");
      setChave("");
      setNomeRecebedor("");
      setValor("");
      setFormato("cartao");
      setBodyText("");
      setComoFunciona(false);
      setActiveModal(true);
    }
  }, [open, data]);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleSave = () => {
    const erro = conferirChave(tipoChave, chave);
    if (erro) {
      toast.error(erro);
      return;
    }
    if (formato === "cartao" && !String(valor).trim()) {
      toast.error("O cartão de pagamento precisa do valor.");
      return;
    }
    const payload = {
      origem: "gerar",
      formato,
      bodyText: bodyText.trim(),
      tipoChave,
      chave: chave.trim(),
      nomeRecebedor: nomeRecebedor.trim(),
      cidade: "SAO PAULO",
      valor: String(valor).trim(),
      txid: "",
      descricao: "",
      codigoVar: "",
      mensagem: "",
      enviarQr: false,
      answerKey: "pix.codigo",
    };
    if (open === "edit") {
      onUpdate({ ...data, data: { ...(data?.data || {}), ...payload } });
    } else {
      onSave(payload);
    }
    handleClose();
  };

  const rotulo = {
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 6,
    display: "block",
  };
  const dica = {
    fontSize: 11.5,
    color: "#6b7280",
    display: "block",
    marginTop: 6,
    lineHeight: 1.5,
  };
  const alerta = {
    fontSize: 11.5,
    color: "#b45309",
    display: "block",
    marginTop: 4,
    lineHeight: 1.5,
    fontWeight: 500,
  };

  return (
    <Dialog open={activeModal} onClose={handleClose} maxWidth="xs" fullWidth scroll="paper">
      <DialogTitle>{open === "create" ? "Adicionar PIX" : "Editar PIX"}</DialogTitle>
      <DialogContent dividers>
        <Box
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            padding: 16,
            marginBottom: 14,
          }}
        >
          <Typography style={rotulo}>O que enviar *</Typography>
          <TextField
            select
            value={formato}
            onChange={(e) => setFormato(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
          >
            <MenuItem value="cartao">Cartão de pagamento (recomendado)</MenuItem>
            <MenuItem value="emv">Código copia e cola</MenuItem>
            <MenuItem value="chave">Só a chave PIX</MenuItem>
          </TextField>
          <Typography style={dica}>
            {formato === "cartao"
              ? "Cartão com o valor em destaque, \"<nome> solicitou um pagamento\" e o botão \"Copiar código Pix\". O código fica escondido dentro do cartão. Exige valor preenchido e só funciona no canal oficial; nos outros cai no código automaticamente."
              : formato === "emv"
              ? "O código do Banco Central vai como texto. O WhatsApp reconhece e mostra o botão \"Copiar código Pix\" embaixo. Funciona em qualquer canal, mas o texto aparece comprido."
              : "Vai só a chave, sem botão nenhum: aparece como um número solto no chat e o cliente copia na mão."}
          </Typography>

          <Typography style={{ ...rotulo, marginTop: 18 }}>Tipo da Chave PIX *</Typography>
          <TextField
            select
            value={tipoChave}
            onChange={(e) => setTipoChave(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
          >
            {TIPOS.map((t) => (
              <MenuItem key={t.v} value={t.v}>
                {t.t}
              </MenuItem>
            ))}
          </TextField>

          <Typography style={{ ...rotulo, marginTop: 18 }}>Chave PIX *</Typography>
          <TextField
            value={chave}
            onChange={(e) => setChave(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
            placeholder={tipoAtual.ex}
          />
          <Typography style={alerta}>
            Confira com atenção — é para essa chave que o dinheiro vai.
          </Typography>

          <Typography style={{ ...rotulo, marginTop: 18 }}>
            Nome da chave PIX
          </Typography>
          <TextField
            value={nomeRecebedor}
            onChange={(e) => setNomeRecebedor(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Nome do titular da chave"
            inputProps={{ maxLength: 25 }}
          />
          <Typography style={dica}>
            Nome de quem é a chave. No cartão aparece como "Fulano solicitou um
            pagamento", e no banco do cliente na hora de confirmar. Se vazio, vai como
            "Pix".
          </Typography>

{(formato === "emv" || formato === "cartao") && (
            <>
          <Typography style={{ ...rotulo, marginTop: 18 }}>Valor (R$)</Typography>
          <TextField
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Ex: 49,90"
          />
          <Typography style={formato === "cartao" ? alerta : dica}>
            {formato === "cartao"
              ? "Obrigatório no cartão — é o valor que aparece em destaque."
              : "Deixe vazio e o cliente digita o valor no banco."}{" "}
            Aceita variável do fluxo, ex.: <b>{"{{valor_plano}}"}</b>
          </Typography>

          {formato === "cartao" && (
            <>
              <Typography style={{ ...rotulo, marginTop: 18 }}>
                Mensagem do cartão
              </Typography>
              <TextField
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Obrigado pela preferência!"
              />
            </>
          )}
            </>
          )}
        </Box>

        <Box
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            padding: 14,
          }}
        >
          <Box
            onClick={() => setComoFunciona((v) => !v)}
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>
              Como funciona
            </Typography>
            <Typography style={{ fontSize: 12, color: "#6b7280" }}>
              {comoFunciona ? "ocultar" : "mostrar"}
            </Typography>
          </Box>

          {comoFunciona && (
            <Box style={{ marginTop: 10 }}>
              <Typography style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.65 }}>
                Este bloco envia o código PIX (copia e cola) para o cliente. Ele toca no
                código, copia e paga no aplicativo do banco. O dinheiro cai direto na sua
                conta — a plataforma não fica no meio. Funciona nas conexões padrão e na
                integração oficial (WhatsApp Cloud API / Meta).
              </Typography>
              <Typography style={{ ...alerta, marginTop: 10 }}>
                O código vai sozinho, numa mensagem separada. Isso é de propósito: no
                WhatsApp um toque copia a mensagem inteira — se tivesse texto junto, o
                banco recusaria o código. Coloque sua mensagem num bloco de texto antes
                deste.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button onClick={handleSave} color="primary" variant="contained">
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowBuilderPixModal;
